import { useMeQuery } from '@/@generated/codegen/API';
import { useAuthTokenContext } from '@/auth/KeycloakProvider';

export const PROFILE_QUERY_KEYS = {
  byId: (userId: string) => ['profiles', userId] as const,
  byUsername: (username: string) => ['profiles', 'username', username] as const,
};

const ONBOARDING_STORAGE_KEY = 'has_seen_onboarding';

export const useProfile = (userId: string | null) => {
  const authContext = useAuthTokenContext();
  const { data, loading, error, refetch } = useMeQuery({
    skip: !userId || authContext.tokenStatus !== 'valid',
  });

  // Get name/username from Keycloak token claims
  // Get email from GraphQL User entity
  // Get has_seen_onboarding from localStorage
  const transformedData =
    data?.me && authContext.tokenStatus === 'valid'
      ? {
          full_name: authContext.email?.split('@')[0] || null, // Fallback: derive from email
          email: data.me.email,
          username: authContext.email?.split('@')[0] || null, // Fallback: derive from email
          has_seen_onboarding:
            localStorage.getItem(`${ONBOARDING_STORAGE_KEY}_${userId}`) === 'true',
        }
      : null;

  return {
    data: transformedData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useProfileByUsername = (username: string | null) => {
  // Cannot query User entity by username - not available in schema
  // Return not-found pattern
  return {
    data: null,
    isLoading: false,
    error: username ? new Error('Username lookup not supported in GraphQL schema') : null,
    refetch: () => Promise.resolve({ data: null }),
  };
};

export const useUpdateProfile = () => {
  return {
    mutate: ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<{ has_seen_onboarding: boolean }>;
    }) => {
      // Update has_seen_onboarding in localStorage
      if (updates.has_seen_onboarding !== undefined) {
        localStorage.setItem(
          `${ONBOARDING_STORAGE_KEY}_${userId}`,
          String(updates.has_seen_onboarding)
        );
      }
    },
    mutateAsync: ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<{ has_seen_onboarding: boolean }>;
    }) => {
      // Update has_seen_onboarding in localStorage
      if (updates.has_seen_onboarding !== undefined) {
        localStorage.setItem(
          `${ONBOARDING_STORAGE_KEY}_${userId}`,
          String(updates.has_seen_onboarding)
        );
      }
      return Promise.resolve({ id: userId, ...updates });
    },
    isPending: false,
    isSuccess: false,
    isError: false,
  };
};
